import { useState, useEffect } from "react";
import { Gift, X, CheckCircle2, Loader2, Copy, Share2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RedeemCouponModal({ open, onClose, onSuccess }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);
  const [myRef, setMyRef] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setCode(""); setErr(null); setSuccess(null); setCopied(false); return;
    }
    fetch(`${API}/api/bonus/my-referral-code`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setMyRef(d))
      .catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/bonus/redeem/${encodeURIComponent(code.trim())}`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.detail || "Falha ao resgatar"); return; }
      setSuccess(d);
      onSuccess?.(d);
    } catch (e) { setErr(String(e)); }
    finally { setBusy(false); }
  };

  const copyRef = async () => {
    if (!myRef?.coupon?.code) return;
    try {
      await navigator.clipboard.writeText(myRef.coupon.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard denied */ }
  };

  if (!open) return null;
  return (
    <div data-testid="redeem-coupon-modal"
         className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-sm border border-fuchsia-400/50 bg-black p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-fuchsia-300">// BONUS</p>
            <h3 className="font-display font-black text-white text-xl tracking-tighter">Resgatar código</h3>
          </div>
          <button onClick={onClose} data-testid="redeem-modal-close"
                  className="text-muted-foreground hover:text-white p-1"><X className="h-4 w-4" /></button>
        </div>

        {success ? (
          <div className="rounded-sm border border-emerald-400/50 bg-emerald-500/10 p-4 space-y-2 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-300 mx-auto" />
            <p className="text-white font-display font-black text-2xl tracking-tighter">
              +${(success.amount_cents / 100).toFixed(2)}
            </p>
            <p className="font-mono text-[10px] tracking-widest text-emerald-200">Bonus Wallet creditada</p>
            <button onClick={onClose} data-testid="redeem-modal-done"
                    className="mt-2 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 font-mono tracking-widest text-[10px] px-4 py-2">
              CONTINUAR
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <span className="font-mono text-[9px] tracking-widest text-muted-foreground">CÓDIGO</span>
              <input value={code} onChange={(e) => setCode(e.target.value)}
                     required minLength={3} autoFocus placeholder="Ex: WELCOME2026"
                     data-testid="redeem-code-input"
                     className="w-full mt-1 bg-black/70 border border-fuchsia-400/30 rounded-sm px-3 py-2 font-mono text-sm text-fuchsia-100 uppercase" />
            </label>
            {err && <p data-testid="redeem-error" className="rounded-sm border border-red-500/50 bg-red-500/10 p-2 font-mono text-xs text-red-300">{err}</p>}
            <button type="submit" disabled={busy || code.trim().length < 3}
                    data-testid="redeem-submit"
                    className="w-full rounded-sm bg-fuchsia-500 text-black hover:bg-fuchsia-400 disabled:opacity-40 font-mono tracking-widest text-xs px-4 py-3 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
              RESGATAR
            </button>
          </form>
        )}

        {/* User's referral code */}
        {myRef?.coupon && (
          <div className="rounded-sm border border-cyan-400/30 bg-cyan-500/5 p-3 space-y-2">
            <p className="font-mono text-[9px] tracking-[0.3em] text-cyan-300 flex items-center gap-1">
              <Share2 className="h-3 w-3" /> SEU CÓDIGO DE INDICAÇÃO
            </p>
            <div className="flex items-center gap-2">
              <code data-testid="my-referral-code" className="font-mono text-sm text-cyan-200 flex-1 break-all">
                {myRef.coupon.code}
              </code>
              <button onClick={copyRef} data-testid="copy-referral"
                      className="text-cyan-300 hover:text-cyan-100 p-1">
                {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <p className="font-mono text-[9px] text-muted-foreground">
              Compartilha com amigos: eles ganham{" "}
              <span className="text-emerald-300">${(myRef.coupon.amount_cents / 100).toFixed(2)}</span>{" "}
              e você ganha <span className="text-emerald-300">${(myRef.reward_cents / 100).toFixed(2)}</span>{" "}
              a cada resgate no Bonus Wallet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
